(function () {
  const fmt = (cents) => {
    const n = Number(cents) / 100;
    return n % 1 === 0 ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`;
  };

  // A color is a pre-order when it has an arrival date, or when it is oversold
  // with no date set (the `backorder` flag from Liquid). The second case never
  // gets to read "Ready to ship" — the date is just still to be confirmed.
  const preorderFrom = (color) => {
    if (!color) return null;
    if (!color.arrival) {
      if (!color.backorder) return null;
      return { etaWeeks: null, shipDate: '', arrival: '', tbd: true, overrideText: color.overrideText || '' };
    }
    const d = new Date(`${color.arrival}T12:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    const weeks = Math.max(1, Math.round((d.getTime() - Date.now()) / 604800000));
    const shipDate = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return {
      etaWeeks: weeks,
      shipDate,
      arrival: color.arrival,
      tbd: false,
      overrideText: color.overrideText || '',
    };
  };

  // The variant's "Pre-order copy" reads as a label for the arrival date
  // ("Expected PRESALE Ship Date: Nov 16"); copy that already names a date
  // (has a digit) is used as written.
  const withDate = (copy, date) => (date && !/\d/.test(copy) ? `${copy}: ${date}` : copy);

  const preorderCopy = (p) => {
    if (p.overrideText) return withDate(p.overrideText, p.tbd ? '' : p.shipDate);
    return p.tbd ? 'Pre-order: ship date coming soon' : `Pre-order: ships ${p.shipDate}`;
  };

  // Value of the visible "Ship date" line property. Shopify shows it under the
  // line in checkout, in the order confirmation email, and on the admin order.
  const shipDateLabel = (p) =>
    p.tbd ? p.overrideText || 'To be confirmed (pre-order)' : `${p.shipDate} (pre-order)`;

  const setStatus = (pill, text, ready, copy) => {
    if (!pill || !text) return;
    pill.classList.toggle('stock-status--ready', ready);
    pill.classList.toggle('stock-status--wait', !ready);
    text.textContent = copy;
  };

  const emitColorChange = (color) => {
    document.dispatchEvent(
      new CustomEvent('slb:color-change', {
        detail: { handle: color.handle, name: color.name, image: color.image },
      })
    );
  };

  const addToCart = async (items) => {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const failure = new Error(err.description || err.message || 'Could not add to cart');
      failure.fromCart = Boolean(err.description);
      throw failure;
    }
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('slb:cart:open'));
    return res.json();
  };

  // Underscore keys are hidden from the shopper and read by the cart drawer and
  // the Shopify Flow that holds fulfillment (`_preorder`). "Ship date" has no
  // underscore on purpose: it is the shopper-facing touchpoint.
  // Each pack added gets its own `_pack_ref`, so two of the same 3-Pack stay
  // two packs in the cart instead of merging into one card of doubled bags.
  const newPackRef = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

  const lineProps = (color, packId, packRef) => {
    const props = { _pack: packId };
    if (packRef) props._pack_ref = packRef;
    const po = preorderFrom(color);
    if (po) {
      props._preorder = 'true';
      if (po.arrival) props._arrival = po.arrival;
      if (po.overrideText) props._override = po.overrideText;
      props['Ship date'] = shipDateLabel(po);
    }
    return props;
  };

  // One alert region for the page, in the DOM before any message lands in it:
  // screen readers skip a live region that arrives together with its text.
  let alertEl = null;
  const announce = (message) => {
    if (!alertEl) return;
    alertEl.textContent = '';
    window.setTimeout(() => {
      alertEl.textContent = message;
    }, 60);
  };

  // Why an add failed, printed under the control that tried it (the buy box
  // CTA row by default) and read out.
  const addErrorCopy = (err) =>
    err && err.fromCart ? err.message : 'Couldn\u2019t add that to your bag. Check your connection and try again.';
  const errorAnchor = (anchor) => anchor || document.querySelector('#buy .cta-row');
  const clearAddError = (anchor) => {
    const host = errorAnchor(anchor);
    const next = host?.nextElementSibling;
    if (next && next.classList.contains('slb-add-error')) next.remove();
  };
  const showAddError = (err, anchor) => {
    const host = errorAnchor(anchor);
    const message = addErrorCopy(err);
    if (host) {
      let el = host.nextElementSibling;
      if (!el || !el.classList.contains('slb-add-error')) {
        el = document.createElement('p');
        el.className = 'slb-add-error';
        host.insertAdjacentElement('afterend', el);
      }
      el.textContent = message;
    }
    announce(message);
    return message;
  };

  // Drawer, menu and dialogs can stack, so the page only scrolls again once
  // the last of them has closed.
  const scrollLocks = new Set();
  const lockScroll = (key, on) => {
    if (on) scrollLocks.add(key);
    else scrollLocks.delete(key);
    document.body.style.overflow = scrollLocks.size ? 'hidden' : '';
  };

  // Every add-to-cart on the page (buy box, bento rail, combo packs) goes
  // through these, so pre-order lines are tagged the same way everywhere.
  const SLB = (window.SLB = window.SLB || {});
  Object.assign(SLB, { preorderFrom, lineProps, newPackRef, addToCart, showAddError, clearAddError, announce, lockScroll });
  SLB.colorForVariant = SLB.colorForVariant || (() => null);

  // WAI-ARIA radio group: the checked radio is the group's one tab stop and
  // the arrow keys move the selection. Returns a re-sync for re-renders.
  const rovingRadios = (group) => {
    if (!group) return () => {};
    const radios = () => Array.from(group.querySelectorAll('[role="radio"]')).filter((r) => !r.disabled);
    const sync = () => {
      const list = radios();
      const checked = list.find((r) => r.getAttribute('aria-checked') === 'true') || list[0];
      list.forEach((r) => r.setAttribute('tabindex', r === checked ? '0' : '-1'));
    };
    const steps = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    group.addEventListener('keydown', (e) => {
      const list = radios();
      const i = list.indexOf(e.target.closest('[role="radio"]'));
      if (i === -1 || e.altKey || e.ctrlKey || e.metaKey) return;
      let next = null;
      if (e.key in steps) next = list[(i + steps[e.key] + list.length) % list.length];
      else if (e.key === 'Home') next = list[0];
      else if (e.key === 'End') next = list[list.length - 1];
      if (!next) return;
      e.preventDefault();
      next.click();
      next.focus();
      sync();
    });
    group.addEventListener('click', sync);
    sync();
    return sync;
  };

  const initBuyBox = () => {
    const dataEl = document.getElementById('buybox-data');
    if (!dataEl) return;
    const data = JSON.parse(dataEl.textContent || '{}');
    const colorByHandle = new Map((data.colors || []).map((c) => [c.handle, c]));
    SLB.colorForVariant = (id) => (data.colors || []).find((c) => c.id === Number(id)) || null;

    let packId = data.defaultPackId || 'single';
    let selectedColor = data.defaultColorHandle;
    let slots = [];
    let pickerSlotIndex = 0;
    let pickerTentative = null;
    let upsellIndex = 0;
    let qty = 1;

    const pack = () => data.packs.find((p) => p.id === packId);

    const $ = (id) => document.getElementById(id);
    const singlePanel = $('single-panel');
    const bundlePanel = $('bundle-panel');
    const bundleSlotsEl = $('bundle-slots');
    const bundleStatus = $('bundle-status');
    const bundleStatusText = $('bundle-status-text');
    const singleStatus = $('single-status');
    const singleStatusText = $('single-status-text');
    const colorLabel = $('color-label');
    const priceCompare = $('price-compare');
    const priceCurrent = $('price-current');
    const pricePer = $('price-per');
    const gwpNote = $('gwp-note');
    const mainCta = $('main-cta');
    const qtyStepper = $('qty-stepper');
    const galleryMain = document.getElementById('gallery-main-img');
    const picker = $('picker-dialog');
    const pickerTitle = $('picker-title');
    const pickerImg = $('picker-img');
    const pickerEmpty = $('picker-empty');
    const pickerColorLabel = $('picker-color-label');
    const pickerSwatches = $('picker-swatches');
    const pickerStatus = $('picker-status');
    const pickerStatusText = $('picker-status-text');
    const pickerCta = $('picker-cta');
    const syncPickerRadios = rovingRadios(pickerSwatches);

    const renderPackSelector = () => {
      document.querySelectorAll('.pack-option').forEach((btn) => {
        btn.setAttribute('aria-checked', String(btn.dataset.packId === packId));
      });
    };

    const slotTile = (handle, index) => {
      if (!handle) {
        // Ghosted placeholder bag (theme asset) with a small plus; falls back
        // to the plain plus when the asset URL is missing.
        const ghost = data.placeholderImage
          ? `<img class="slot-img slot-img--ghost" src="${data.placeholderImage}" alt="" loading="lazy" /><span class="slot-plus slot-plus--badge" aria-hidden="true">+</span>`
          : `<span class="slot-plus">+</span>`;
        return `
          <button class="slot slot--empty" type="button" data-slot-index="${index}" aria-label="Pick a color for bag ${index + 1}">
            ${ghost}
            <span class="slot-name">pick a color</span>
          </button>`;
      }
      const color = colorByHandle.get(handle);
      const po = preorderFrom(color);
      const eta = po ? `<span class="slot-eta">${po.tbd ? 'pre-order' : `~${po.etaWeeks}wk`}</span>` : '';
      return `
        <button class="slot slot--filled" type="button" data-slot-index="${index}" aria-label="Bag ${index + 1}: ${color.name}. Tap to change.">
          ${eta}
          <img class="slot-img" src="${color.image}" alt="" loading="lazy" />
          <span class="slot-name">${color.name}</span>
        </button>`;
    };

    const renderSlots = () => {
      if (!bundleSlotsEl) return;
      bundleSlotsEl.innerHTML = slots.map((h, i) => slotTile(h, i)).join('');
      const preorders = slots
        .filter(Boolean)
        .map((h) => preorderFrom(colorByHandle.get(h)))
        .filter(Boolean);
      if (preorders.length) {
        // The pack ships as one parcel, so the latest arrival sets the date.
        const latest = preorders.reduce((a, b) => (b.arrival > a.arrival ? b : a));
        const copy = latest.arrival
          ? `Ships together ${latest.shipDate}`
          : 'Ships together: date coming soon';
        setStatus(bundleStatus, bundleStatusText, false, copy);
      } else {
        setStatus(bundleStatus, bundleStatusText, true, 'Ready to ship');
      }
      bundleSlotsEl.querySelectorAll('.slot').forEach((el) => {
        el.addEventListener('click', () => openPicker(Number(el.dataset.slotIndex)));
      });
    };

    const renderSinglePreorder = () => {
      const color = colorByHandle.get(selectedColor);
      const po = preorderFrom(color);
      if (po) setStatus(singleStatus, singleStatusText, false, preorderCopy(po));
      else setStatus(singleStatus, singleStatusText, true, 'Ready to ship');
    };

    const renderPanels = () => {
      const isSingle = packId === 'single';
      if (singlePanel) singlePanel.hidden = !isSingle;
      if (bundlePanel) bundlePanel.hidden = isSingle;
      if (qtyStepper) qtyStepper.style.display = isSingle ? '' : 'none';
      if (!isSingle) renderSlots();
      renderSinglePreorder();
    };

    // What the cart charges for n single bags. Kaching's quantity breaks
    // price the line, not the pack picker: 2 bags cost the 2-Pack price and
    // every bag from 3 up costs the 3-Pack's per-bag price.
    const singleTotal = (n) => {
      const unit = data.unitPrice || pack().price;
      const p2 = data.packs.find((x) => x.id === 'pack2');
      const p3 = data.packs.find((x) => x.id === 'pack3');
      if (n >= 3 && p3) return Math.round(p3.price / p3.slots) * n;
      if (n === 2 && p2) return p2.price;
      return unit * n;
    };

    const renderPrice = () => {
      const p = pack();
      if (!p || !priceCurrent) return;
      if (p.id === 'single') {
        const total = singleTotal(qty);
        const full = (data.unitPrice || p.price) * qty;
        const saves = full > total;
        if (priceCompare) {
          priceCompare.hidden = !saves;
          priceCompare.textContent = saves ? fmt(full) : '';
        }
        if (pricePer) {
          const perLine = qty >= 2 ? `${fmt(total / qty)}/bag, ships free` : '';
          pricePer.hidden = !perLine;
          pricePer.textContent = perLine;
        }
        priceCurrent.textContent = fmt(total);
        priceCurrent.classList.toggle('is-deal', saves);
      } else {
        // A pack with no price break (2-Pack: its perk is shipping) must not
        // show the same number struck through next to itself.
        const saves = p.compareAt > p.price;
        if (priceCompare) {
          priceCompare.hidden = !saves;
          priceCompare.textContent = saves ? fmt(p.compareAt) : '';
        }
        if (pricePer) {
          pricePer.hidden = false;
          pricePer.textContent = `${fmt(p.price / p.slots)}/bag${p.freeShipping ? ', ships free' : ''}`;
        }
        priceCurrent.textContent = fmt(p.price);
        priceCurrent.classList.toggle('is-deal', saves);
      }
      if (gwpNote) {
        gwpNote.hidden = !p.gift;
        if (p.gift) gwpNote.textContent = p.gift;
      }
    };

    const renderCta = () => {
      if (!mainCta) return;
      const p = pack();
      mainCta.disabled = false;
      if (p.id === 'single') {
        const color = colorByHandle.get(selectedColor);
        mainCta.textContent = preorderFrom(color) ? 'Pre-Order' : 'Add to Cart';
        return;
      }
      const firstEmpty = slots.findIndex((s) => !s);
      if (firstEmpty !== -1) {
        mainCta.disabled = true;
        mainCta.textContent = `Pick Bag ${firstEmpty + 1}'s Color`;
      } else {
        const hasPreorder = slots.some((h) => preorderFrom(colorByHandle.get(h)));
        mainCta.textContent = hasPreorder ? 'Pre-Order Bundle' : 'Add Bundle to Cart';
      }
    };

    const renderAll = () => {
      renderPackSelector();
      renderPanels();
      renderPrice();
      renderCta();
    };

    const renderPickerSwatches = () => {
      if (!pickerSwatches) return;
      pickerSwatches.style.setProperty('--picker-cols', Math.ceil(data.colors.length / 2));
      pickerSwatches.innerHTML = data.colors
        .map((c) => {
          const usedBy = slots.findIndex((h, i) => h === c.handle && i !== pickerSlotIndex);
          const checked = pickerTentative === c.handle;
          const inBag = usedBy !== -1 ? ` (in bag ${usedBy + 1})` : '';
          return `
            <button class="swatch" type="button" role="radio" aria-checked="${checked}" data-picker-handle="${c.handle}" aria-label="${c.name}${inBag}">
              <span class="swatch-dot" style="background:${c.swatch}"></span>
              ${usedBy !== -1 ? `<span class="swatch-slot" aria-hidden="true">${usedBy + 1}</span>` : ''}
            </button>`;
        })
        .join('');
      pickerSwatches.querySelectorAll('.swatch').forEach((el) => {
        el.addEventListener('click', () => {
          pickerTentative = el.dataset.pickerHandle;
          renderPickerState();
        });
      });
      syncPickerRadios();
    };

    const renderPickerState = () => {
      const color = pickerTentative ? colorByHandle.get(pickerTentative) : null;
      if (pickerTitle) pickerTitle.textContent = `BAG ${pickerSlotIndex + 1} OF ${slots.length} — PICK A COLOR`;
      if (color) {
        if (pickerImg) {
          pickerImg.src = color.image;
          pickerImg.hidden = false;
          pickerImg.classList.remove('is-ghost');
        }
        if (pickerEmpty) pickerEmpty.hidden = true;
        if (pickerColorLabel) pickerColorLabel.textContent = color.name;
        if (pickerCta) {
          pickerCta.disabled = false;
          pickerCta.textContent = `Add ${color.name} to Bundle`;
        }
        const po = preorderFrom(color);
        if (po) setStatus(pickerStatus, pickerStatusText, false, preorderCopy(po));
        else setStatus(pickerStatus, pickerStatusText, true, 'Ready to ship');
        if (pickerStatus) pickerStatus.hidden = false;
      } else {
        // Nothing picked yet: the section's default photo, else the ghosted
        // placeholder bag, else a plain tile.
        const defaultSrc = pickerImg?.dataset.defaultSrc || '';
        if (pickerImg) {
          if (defaultSrc) pickerImg.src = defaultSrc;
          pickerImg.hidden = !defaultSrc;
          pickerImg.classList.toggle('is-ghost', Boolean(defaultSrc) && pickerImg.dataset.defaultGhost === 'true');
        }
        if (pickerEmpty) pickerEmpty.hidden = Boolean(defaultSrc);
        if (pickerColorLabel) pickerColorLabel.textContent = '—';
        if (pickerCta) {
          pickerCta.disabled = true;
          pickerCta.textContent = 'Pick a Color';
        }
        if (pickerStatus) pickerStatus.hidden = true;
      }
      pickerSwatches?.querySelectorAll('.swatch').forEach((el) => {
        el.setAttribute('aria-checked', String(el.dataset.pickerHandle === pickerTentative));
      });
      syncPickerRadios();
    };

    const openPicker = (slotIndex) => {
      pickerSlotIndex = slotIndex;
      pickerTentative = slots[slotIndex] ?? null;
      renderPickerSwatches();
      renderPickerState();
      if (!picker || picker.open) return;
      picker.showModal();
      lockScroll('picker', true);
    };

    // Picking a color rebuilds the slots, so the dialog's own focus return
    // lands on a detached button. Send focus to the slot that was edited.
    picker?.addEventListener('close', () => {
      lockScroll('picker', false);
      if (!bundlePanel || bundlePanel.hidden) return;
      bundleSlotsEl?.querySelector(`[data-slot-index="${pickerSlotIndex}"]`)?.focus();
    });

    pickerCta?.addEventListener('click', () => {
      if (!pickerTentative) return;
      slots[pickerSlotIndex] = pickerTentative;
      picker?.close();
      renderSlots();
      renderCta();
      const pickedColor = colorByHandle.get(pickerTentative);
      showColorPhotos(pickedColor);
      if (pickedColor) emitColorChange(pickedColor);
      const nextEmpty = slots.findIndex((s) => !s);
      if (nextEmpty !== -1) setTimeout(() => openPicker(nextEmpty), 180);
    });

    $('picker-close')?.addEventListener('click', () => picker?.close());
    picker?.addEventListener('click', (e) => {
      if (e.target === picker) picker.close();
    });

    document.querySelectorAll('.pack-option').forEach((btn) => {
      btn.addEventListener('click', () => {
        const nextId = btn.dataset.packId;
        if (nextId === packId) return;
        const prevSlots = slots;
        packId = nextId;
        const p = pack();
        if (p.id === 'single') {
          slots = [];
          // The gallery may still show a bundle bag's color; Single buys selectedColor.
          showColorPhotos(colorByHandle.get(selectedColor));
        } else {
          slots = Array.from({ length: p.slots }, (_, i) =>
            prevSlots[i] !== undefined ? prevSlots[i] : i === 0 ? selectedColor : null
          );
          if (!slots[0]) slots[0] = selectedColor;
        }
        renderAll();
      });
    });

    document.querySelectorAll('#single-panel .swatch').forEach((el) => {
      el.addEventListener('click', () => {
        selectedColor = el.dataset.colorHandle;
        const color = colorByHandle.get(selectedColor);
        if (colorLabel && color) colorLabel.textContent = color.name;
        showColorPhotos(color);
        if (color) emitColorChange(color);
        document.querySelectorAll('#single-panel .swatch').forEach((s) => {
          s.setAttribute('aria-checked', String(s.dataset.colorHandle === selectedColor));
        });
        renderSinglePreorder();
        renderCta();
      });
    });

    // Gallery. Each thumb carries the color handles it belongs to (variant
    // image or alt text naming the color, via the slb-media-colors snippet).
    // A thumb with no color shows for every color.
    const thumbRail = $('gallery-thumbs');
    const thumbs = Array.from(document.querySelectorAll('.thumb'));
    const thumbColors = (t) => (t.dataset.colors || '').split(',').filter(Boolean);
    const thumbsFor = (handle) => thumbs.filter((t) => {
      const list = thumbColors(t);
      return list.length === 0 || list.includes(handle);
    });

    // A color with no image of its own borrows its first tagged photo, so the
    // gallery, picker, and bundle slots never fall back to another color.
    (data.colors || []).forEach((c) => {
      if (c.mediaId) return;
      const own = thumbs.find((t) => thumbColors(t).includes(c.handle));
      if (own) {
        c.image = own.dataset.thumbSrc;
        c.mediaId = own.dataset.mediaId;
      }
    });

    // Scrolls the rail (vertical on desktop, horizontal on mobile) so the
    // active thumb is in view. Never scrolls the page.
    const revealThumb = (t) => {
      if (!thumbRail || !t) return;
      const r = thumbRail.getBoundingClientRect();
      const b = t.getBoundingClientRect();
      if (b.top < r.top) thumbRail.scrollTop += b.top - r.top;
      else if (b.bottom > r.bottom) thumbRail.scrollTop += b.bottom - r.bottom;
      if (b.left < r.left) thumbRail.scrollLeft += b.left - r.left;
      else if (b.right > r.right) thumbRail.scrollLeft += b.right - r.right;
    };

    const setActiveThumb = (active) => {
      thumbs.forEach((t) => {
        t.classList.toggle('is-active', t === active);
        if (t === active) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      revealThumb(active);
    };

    // The main photo's alt describes the photo on show: its own alt text
    // when it has one (thumbs fall back to "Product photo"), else the
    // product and the color being shown.
    const altBase = galleryMain ? galleryMain.alt.replace(/\s+—\s+[^—]*$/, '') : '';
    let shownColor = null;
    const setMainAlt = (thumb) => {
      if (!galleryMain) return;
      const own = (thumb?.getAttribute('aria-label') || '').trim();
      if (own && own !== 'Product photo') galleryMain.alt = own;
      else if (shownColor) galleryMain.alt = `${altBase} — ${shownColor.name}`;
    };

    const showColorPhotos = (color) => {
      if (!color) return;
      shownColor = color;
      let visible = thumbsFor(color.handle);
      // Nothing tagged for this color and nothing generic: show the whole set
      // rather than an empty rail.
      if (visible.length === 0) visible = thumbs;
      thumbs.forEach((t) => { t.hidden = !visible.includes(t); });
      if (thumbRail) thumbRail.dataset.count = String(visible.length);
      const active = visible.find((t) => t.dataset.mediaId === String(color.mediaId)) || visible[0];
      setActiveThumb(active);
      if (!galleryMain) return;
      const src = active ? active.dataset.thumbSrc : color.image;
      if (galleryMain.getAttribute('src') !== src) galleryMain.src = src;
      setMainAlt(active);
    };

    thumbs.forEach((el) => {
      el.addEventListener('click', () => {
        if (galleryMain) galleryMain.src = el.dataset.thumbSrc;
        setActiveThumb(el);
        setMainAlt(el);
      });
    });

    showColorPhotos(colorByHandle.get(selectedColor));

    const qtyValue = $('qty-value');
    const qtyMinus = $('qty-minus');
    const qtyPlus = $('qty-plus');
    // One below the cart drawer's team order threshold: bigger orders go
    // through Meredith as team orders.
    const teamBags = Number(document.getElementById('cart-root')?.dataset.teamBags) || 0;
    const qtyMax = teamBags > 1 ? teamBags - 1 : 9;
    const renderQty = () => {
      if (qtyValue) qtyValue.textContent = String(qty);
      if (qtyMinus) qtyMinus.disabled = qty <= 1;
      if (qtyPlus) qtyPlus.disabled = qty >= qtyMax;
    };
    qtyMinus?.addEventListener('click', () => {
      qty = Math.max(1, qty - 1);
      renderQty();
      renderPrice();
    });
    qtyPlus?.addEventListener('click', () => {
      qty = Math.min(qtyMax, qty + 1);
      renderQty();
      renderPrice();
    });
    renderQty();

    mainCta?.addEventListener('click', async () => {
      if (mainCta.disabled) return;
      const p = pack();
      const original = mainCta.textContent;
      mainCta.disabled = true;
      clearAddError();
      try {
        if (p.id === 'single') {
          const color = colorByHandle.get(selectedColor);
          await addToCart([
            {
              id: color.id,
              quantity: qty,
              properties: lineProps(color, 'single'),
            },
          ]);
        } else {
          const packRef = newPackRef();
          const items = slots.map((h) => {
            const color = colorByHandle.get(h);
            return {
              id: color.id,
              quantity: 1,
              properties: lineProps(color, p.id, packRef),
            };
          });
          await addToCart(items);
        }
        mainCta.textContent = 'Added';
        setTimeout(() => {
          mainCta.textContent = original;
          mainCta.disabled = false;
          renderCta();
        }, 1400);
      } catch (err) {
        showAddError(err);
        mainCta.textContent = 'Try again';
        mainCta.disabled = false;
        setTimeout(() => {
          mainCta.textContent = original;
          renderCta();
        }, 1800);
      }
    });

    const renderUpsell = () => {
      if (!data.crossSell || !data.crossSell.length) return;
      const item = data.crossSell[upsellIndex];
      const img = $('upsell-img');
      if (img) img.src = item.image;
      const title = $('upsell-title');
      if (title) title.textContent = item.title;
      const blurb = $('upsell-blurb');
      if (blurb) blurb.textContent = item.blurb;
      const compare = $('upsell-compare');
      if (compare) {
        compare.hidden = !(item.bundlePrice < item.price);
        compare.textContent = fmt(item.price);
      }
      const current = $('upsell-current');
      if (current) current.textContent = fmt(item.bundlePrice);
      const variantSelect = $('upsell-select');
      if (variantSelect) {
        variantSelect.innerHTML = (item.options || [])
          .map((opt) => `<option value="${opt.id}">${opt.label}</option>`)
          .join('');
        variantSelect.setAttribute('aria-label', item.optionLabel || 'Variant');
      }
    };

    const pageUpsell = (dir) => {
      const n = data.crossSell.length;
      if (!n) return;
      upsellIndex = (upsellIndex + dir + n) % n;
      renderUpsell();
    };

    $('upsell-prev')?.addEventListener('click', () => pageUpsell(-1));
    $('upsell-next')?.addEventListener('click', () => pageUpsell(1));
    $('upsell-add')?.addEventListener('click', async () => {
      const btn = $('upsell-add');
      const select = $('upsell-select');
      const variantId = Number(select?.value);
      if (!variantId || !btn) return;
      const original = btn.textContent;
      const actions = btn.closest('.upsell-actions');
      btn.disabled = true;
      clearAddError(actions);
      try {
        await addToCart([
          {
            id: variantId,
            quantity: 1,
            properties: { _pack: 'cross-sell' },
          },
        ]);
        btn.textContent = 'Added';
      } catch (err) {
        showAddError(err, actions);
        btn.textContent = 'Try again';
      }
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1200);
    });

    const gDialog = $('guarantee-dialog');
    // Every guarantee button on the page (buy box, founder note) opens the
    // one dialog, which lives in this section.
    document.querySelectorAll('[data-guarantee-open]').forEach((b) => {
      b.addEventListener('click', () => {
        if (!gDialog || gDialog.open) return;
        gDialog.showModal();
        lockScroll('guarantee', true);
      });
    });
    $('guarantee-close')?.addEventListener('click', () => gDialog?.close());
    gDialog?.addEventListener('click', (e) => {
      if (e.target === gDialog) gDialog.close();
    });
    gDialog?.addEventListener('close', () => lockScroll('guarantee', false));

    rovingRadios(document.querySelector('.pack-selector'));
    rovingRadios(singlePanel?.querySelector('.swatch-grid'));

    renderUpsell();
    renderAll();
  };

  const boot = () => {
    if (!alertEl) {
      alertEl = document.createElement('p');
      alertEl.className = 'sr-only';
      alertEl.setAttribute('role', 'alert');
      document.body.appendChild(alertEl);
    }
    initBuyBox();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
